import { useEffect, useState } from 'react';
import { Badge, Card, Col, Row, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Section from 'components/base/Section';
import { Order, orderService } from 'services/api';
import { useAuth } from 'providers/AuthProvider';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';

const AllOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const ordersData = await orderService.listAllOrders();
        setOrders(ordersData);
      } catch (err) {
        console.error('Erro ao carregar pedidos:', err);
        setError('Erro ao carregar os pedidos');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated]);

  const formatCurrency = (value: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(parseFloat(value));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge bg="success">Aprovado</Badge>;
      case 'pending':
        return <Badge bg="warning">Pendente</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejeitado</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const MessageCell = ({ message }: { message: string }) => {
    if (!message) {
      return <small className="text-muted">Sem mensagem</small>;
    }

    return (
      <div style={{ maxWidth: '500px' }}>
        <small className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
          {message}
        </small>
      </div>
    );
  };

  const columns: ColumnDef<Order>[] = [
    {
      accessorKey: 'orderId',
      header: 'ID do Pedido',
      cell: ({ row }) => (
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">
            {row.original.orderId.substring(0, 8)}...
          </small>
          {/* <Link to={`/shop/order/${row.original.orderId}`} className="text-primary">
            <FontAwesomeIcon icon={faEye} />
          </Link> */}
        </div>
      )
    },
    {
      accessorKey: 'cartValue',
      header: 'Valor',
      cell: ({ row }) => (
        <strong>{formatCurrency(row.original.cartValue)}</strong>
      )
    },
    {
      accessorKey: 'paymentStatus',
      header: 'Status do Pagamento',
      cell: ({ row }) => getPaymentStatusBadge(row.original.paymentStatus)
    },
    {
      accessorKey: 'createdAt',
      header: 'Data do Pedido',
      cell: ({ row }) => (
        <small>{formatDate(row.original.createdAt)}</small>
      )
    },
    {
      accessorKey: 'paymentDate',
      header: 'Data do Pagamento',
      cell: ({ row }) => (
        <small>{formatDate(row.original.paymentDate)}</small>
      )
    },
    {
      accessorKey: 'isAnonymous',
      header: 'Anônimo',
      cell: ({ row }) => (
        row.original.isAnonymous ? (
          <Badge bg="secondary">Sim</Badge>
        ) : (
          <Badge bg="info">Não</Badge>
        )
      )
    },
    {
      accessorKey: 'message',
      header: 'Mensagem',
      size: 400,
      cell: ({ row }) => <MessageCell message={row.original.message} />
    }
  ];

  const table = useAdvanceTable({
    data: orders,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  if (!isAuthenticated) {
    return (
      <Section small className="py-5">
        <div className="text-center">
          <p>Você precisa estar logado para ver os pedidos.</p>
        </div>
      </Section>
    );
  }

  if (loading) {
    return (
      <Section small className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section small className="py-5">
        <div className="text-center">
          <p className="text-danger">{error}</p>
        </div>
      </Section>
    );
  }

  return (
    <div className="pt-5 mb-9">
      <Section small className="py-0">
        <Row className="align-items-center justify-content-between g-3 mb-4">
          <Col xs="auto">
            <h2 className="mb-0">Todos os Pedidos</h2>
          </Col>
          <Col xs="auto">
            <p className="mb-0 text-muted">Total de {orders.length} pedidos encontrados</p>
          </Col>
        </Row>

        <Card>
          <Card.Body className="p-0">
            {orders.length === 0 ? (
              <div className="text-center p-5">
                <p className="mb-0">Nenhum pedido encontrado.</p>
              </div>
            ) : (
              <AdvanceTableProvider {...table}>
                <AdvanceTable 
                  tableProps={{
                    className: 'mb-0',
                    hover: true
                  }}
                  headerClassName="bg-light"
                />
                <AdvanceTableFooter />
              </AdvanceTableProvider>
            )}
          </Card.Body>
        </Card>
      </Section>
    </div>
  );
};

export default AllOrders;